import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Suspense & Lazy Loading
//  Chapter 8 · Performance
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `function DashboardPage() {
  return (
    <div>
      {/* Header loads first — not suspended */}
      <DashboardHeader />

      <div style={{ display: 'flex' }}>
        {/* Sidebar has its own loading boundary */}
        <Suspense fallback={<SidebarSkeleton />}>
          <DashboardSidebar />
        </Suspense>

        {/* Main content has its own loading boundary */}
        <main>
          <Suspense fallback={<StatsSkeleton />}>
            <StatsSection />    {/* Loads independently */}
          </Suspense>

          <Suspense fallback={<TableSkeleton />}>
            <RecentOrdersTable />  {/* Loads independently */}
          </Suspense>
        </main>
      </div>
    </div>
  );
}`;

const CODE_2 = `import { useState, useTransition, Suspense } from 'react';

function TabsPage() {
  const [tab, setTab]             = useState('posts');
  const [isPending, startTransition] = useTransition();

  function switchTab(newTab) {
    startTransition(() => {
      setTab(newTab);    // Non-urgent — current tab stays visible during load
    });
  }

  return (
    <div style={{ opacity: isPending ? 0.7 : 1 }}>
      <TabBar activeTab={tab} onTabChange={switchTab} />
      {isPending && <p>Loading...</p>}
      <Suspense fallback={<TabSkeleton />}>
        {tab === 'posts'    && <PostsTab />}
        {tab === 'comments' && <CommentsTab />}
        {tab === 'likes'    && <LikesTab />}
      </Suspense>
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function SuspenseAndLazyLoading() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 8 · Performance</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Suspense & Lazy Loading</h1>
        <p style={styles.desc}>Use Suspense for both code splitting and data fetching in modern React.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Suspense Overview ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Suspense Overview</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Suspense lets components &#39;wait&#39; for something before rendering. Originally for code splitting (React.lazy), it now also supports data fetching (with Suspense-enabled libraries like TanStack Query, SWR, or Relay). The parent shows a fallback UI while the child is &#39;suspended&#39;." }} />
      </div>

      /* ── Nested Suspense Boundaries ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Nested Suspense Boundaries</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Multiple Suspense boundaries give fine-grained loading control — different parts of the page can load independently." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── React 18 — useTransition with Suspense ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>React 18 — useTransition with Suspense</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "useTransition marks a state update as non-urgent, keeping the current UI interactive while new content loads." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the difference between Suspense for code splitting and Suspense for data?</div>
          <div style={styles.qItem}>✦ 2. What does isPending represent in useTransition?</div>
          <div style={styles.qItem}>✦ 3. Can you have multiple fallbacks with nested Suspense boundaries?</div>
          <div style={styles.qItem}>✦ 4. What libraries support Suspense for data fetching?</div>
          <div style={styles.qItem}>✦ 5. Build a page with independent Suspense boundaries for a header, sidebar, and three content sections.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
